import { getFirestore, collection, query, orderBy, getDocs, limit, doc, getDoc, updateDoc, arrayUnion, arrayRemove, where, writeBatch } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const AdminAnalyticsService = {
    /**
     * [SWIS] Get Real-time System Overview (Ticker)
     * Fetches from `stats_daily_system` for instant loading.
     */
    getOverviewStats: async () => {
        try {
            const todayStr = new Date().toISOString().split('T')[0];
            const docRef = doc(db, 'stats_daily_system', todayStr);
            const snapshot = await getDoc(docRef);

            if (snapshot.exists()) {
                return snapshot.data();
            }
            return { total_revenue_krw: 0, total_minted_stars: 0 };
        } catch (error) {
            console.error("AdminAnalyticsService.getOverviewStats Error:", error);
            return { total_revenue_krw: 0, total_minted_stars: 0 };
        }
    },

    /**
     * [SWIS] Get Series Performance Report
     * Aggregates `stats_daily_series` for a given date range (Default: Today).
     * For Amazon-grade scale, this should ideally be a Cloud Function, 
     * but for now we query the pre-aggregated daily stats.
     */
    getSeriesReport: async (dateStr = null) => {
        const targetDate = dateStr || new Date().toISOString().split('T')[0];
        try {
            const q = query(
                collection(db, 'stats_daily_series'),
                where('date', '==', targetDate),
                orderBy('total_revenue_star', 'desc'),
                limit(100)
            );
            const snapshot = await getDocs(q);

            // Join with Series Title (Optimization: Cache series titles or fetch on demand)
            // For dashboard table, we map results.
            const stats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Enrich with Titles (Batch Fetch if possible, or individual)
            // Ideally `stats_daily_series` should contain 'title' snapshot for perf.
            // If not, we fetch series details.
            // Let's assume we need to fetch titles.
            const enriched = await Promise.all(stats.map(async (stat) => {
                // Optimization: In real app, name is denormalized in stat doc.
                // Fallback to fetching series doc.
                if (stat.series_title) return stat;

                try {
                    const sSnap = await getDoc(doc(db, 'series', stat.series_id));
                    return { ...stat, series_title: sSnap.exists() ? sSnap.data().title : 'Unknown' };
                } catch (e) { return { ...stat, series_title: 'Error' }; }
            }));

            return enriched;

        } catch (error) {
            console.error("AdminAnalyticsService.getSeriesReport Error:", error);
            return [];
        }
    },

    /**
     * [SWIS] Get Author Settlement Candidates
     * Fetches `stats_daily_author` and calculates payout.
     * Scale: Supports thousands of authors via monthly aggregation.
     */
    getSettlementCandidates: async (dateStr = null, feePercent = 70) => {
        const targetDate = dateStr || new Date().toISOString().split('T')[0];
        try {
            const q = query(
                collection(db, 'stats_daily_author'),
                where('date', '==', targetDate),
                orderBy('total_revenue', 'desc'),
                limit(200)
            );
            const snapshot = await getDocs(q);

            const authors = await Promise.all(snapshot.docs.map(async (docSnap) => {
                const stat = docSnap.data();
                // Fetch Author Name
                let name = 'Unknown';
                try {
                    const uSnap = await getDoc(doc(db, 'users', stat.author_id));
                    if (uSnap.exists()) name = uSnap.data().displayName || uSnap.data().email;
                } catch (e) { }

                // Calculate Payout (Dynamic Fee)
                const revenue = stat.total_revenue || 0;
                const platformFee = Math.floor(revenue * (feePercent / 100)); // Variable Fee
                const tax = Math.floor(revenue * 0.033); // 3.3% Withholding Tax
                const payout = revenue - platformFee - tax;

                return {
                    id: stat.author_id,
                    name,
                    novel_rev: stat.novel_revenue || 0,
                    toon_rev: stat.toon_revenue || 0,
                    davinci_rev: stat.davinci_revenue || 0,
                    total_revenue: revenue,
                    platform_fee: platformFee,
                    tax: tax,
                    final_payout: payout
                };
            }));

            return authors;

        } catch (error) {
            console.error("AdminAnalyticsService.getSettlementCandidates Error:", error);
            return [];
        }
    },

    /**
     * [TRENDS] Get Trending Series
     * Fetches series ordered by viewCount (or recently updated).
     */
    /**
     * [TRENDS] Get Trending Series
     * Fetches series ordered by viewCount and Enriches with Author Name & Thumbnail.
     */
    getTrendingContent: async (limitCount = 10) => {
        try {
            // [FIX] Fetch buffer (x3) to allow filtering of invalid/ghost data
            const q = query(
                collection(db, 'series'),
                orderBy('viewCount', 'desc'),
                limit(limitCount * 3)
            );
            const snapshot = await getDocs(q);
            const rawSeries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // [FIX] Filter Invalid Data (Ghost Docs, Missing Titles) AND Published Check
            const validSeries = rawSeries.filter(s => {
                if (!s.title || s.title === 'undefined') return false;
                // Optional: valid statuses only? For now, just ensure it has content.
                return true;
            });

            // [FIX] Enrich with Author Name (Join User) and Correct Thumbnail
            const enriched = await Promise.all(validSeries.slice(0, limitCount).map(async (s) => {
                // 1. Resolve Author Name
                let authorName = 'Unknown Author';
                if (s.authorId) {
                    try {
                        // Optimization: In a real app, use a DataLoader or batch fetch.
                        // Here we fetch individual for simplicity (Limit 10 is small).
                        const uSnap = await getDoc(doc(db, 'users', s.authorId));
                        if (uSnap.exists()) {
                            const uData = uSnap.data();
                            authorName = uData.displayName || uData.email?.split('@')[0] || 'Unknown';
                        }
                    } catch (e) { /* Ignore user fetch error */ }
                }

                // 2. Resolve Thumbnail (Prioritize Metadata)
                // Schema variations: metadata.thumbnail, thumbnail, coverImage, or imageUrls[0]
                const thumbnail = s.metadata?.thumbnail || s.thumbnail || s.coverImage || (s.imageUrls && s.imageUrls[0]) || null;

                return {
                    ...s,
                    authorName,
                    thumbnailUrl: thumbnail
                };
            }));

            return enriched;
        } catch (error) {
            console.error("AdminAnalyticsService.getTrendingContent Error:", error);
            return [];
        }
    },

    /**
     * [TRENDS] Get Trending Feeds (Plaza)
     * Fetches feeds ordered by likeCount.
     */
    /**
     * [TRENDS] Get Trending Feeds (Plaza)
     * Fetches feeds ordered by likes (int) or likedBy (array size).
     */
    getTrendingFeeds: async (limitCount = 10) => {
        try {
            // [FIX] Try sorting by 'likes' (integer field) first. 
            // If index missing, might fail, but 'likes' is the standard field now.
            const q = query(
                collection(db, 'plaza_feeds'),
                orderBy('likes', 'desc'),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    // [FIX] Normalize Like Count
                    likeCount: (typeof data.likes === 'number') ? data.likes : (data.likedBy?.length || 0)
                };
            });
        } catch (error) {
            console.error("AdminAnalyticsService.getTrendingFeeds Error:", error);
            return [];
        }
    },

    /**
     * [GROWTH] Get Most Shared Content (Viral Analytics)
     * Fetches from `analytics_share_hits` ordered by total hits.
     */
    getMostSharedContent: async (limitCount = 5) => {
        try {
            const q = query(
                collection(db, 'analytics_share_hits'),
                orderBy('totalHits', 'desc'),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);

            // Return raw data (already contains title, type, counters)
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("AdminAnalyticsService.getMostSharedContent Error:", error);
            return [];
        }
    },

    /**
     * [DEV] Seed Mock Data
     */
    seedMockData: async () => {
        try {
            console.log("Generating Mock Data...");
            const todayStr = new Date().toISOString().split('T')[0];
            const batch = writeBatch(db);

            // 1. Mock System Stats
            const systemRef = doc(db, 'stats_daily_system', todayStr);
            batch.set(systemRef, {
                date: todayStr,
                total_revenue_krw: Math.floor(Math.random() * 5000000) + 1000000,
                total_minted_stars: Math.floor(Math.random() * 10000) + 1000,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            // 2. Mock Series Stats (5 items)
            for (let i = 1; i <= 5; i++) {
                const id = `mock-series-${i}`;
                const ref = doc(db, 'stats_daily_series', `${todayStr}_${id}`);
                batch.set(ref, {
                    date: todayStr,
                    series_id: id,
                    series_title: `[MOCK] Series Title ${i}`,
                    author_id: `mock-author-${i}`,
                    total_revenue_star: Math.floor(Math.random() * 5000) + 100,
                    novel_revenue: Math.floor(Math.random() * 2500),
                    toon_revenue: Math.floor(Math.random() * 2500),
                    viewCount: Math.floor(Math.random() * 1000),
                }, { merge: true });
            }

            // 3. Mock Author Stats (5 items)
            for (let i = 1; i <= 5; i++) {
                const id = `mock-author-${i}`;
                const ref = doc(db, 'stats_daily_author', `${todayStr}_${id}`);
                const rev = Math.floor(Math.random() * 500000) + 50000;
                batch.set(ref, {
                    date: todayStr,
                    author_id: id,
                    author_name: `[MOCK] Author ${i}`,
                    total_revenue: rev,
                    novel_revenue: Math.floor(rev * 0.5),
                    toon_revenue: Math.floor(rev * 0.5),
                    davinci_revenue: 0,
                    platform_fee: Math.floor(rev * 0.3),
                    tax: Math.floor(rev * 0.033)
                }, { merge: true });
            }

            await batch.commit();
            console.log("Mock Data Generated Successfully");
            return true;
        } catch (e) {
            console.error("Mock Data Generation Failed:", e);
            return false;
        }
    },

    /**
     * [DEV] Clear Mock Data
     */
    clearMockData: async () => {
        try {
            console.log("Clearing Mock Data...");
            const todayStr = new Date().toISOString().split('T')[0];
            const batch = writeBatch(db);

            // 1. Clear System Stats (Reset to 0)
            const systemRef = doc(db, 'stats_daily_system', todayStr);
            batch.delete(systemRef); // Or set to 0? Delete is cleaner if it was purely mock.

            // 2. Clear Mock Series Stats
            for (let i = 1; i <= 5; i++) {
                const id = `mock-series-${i}`;
                const ref = doc(db, 'stats_daily_series', `${todayStr}_${id}`);
                batch.delete(ref);
            }

            // 3. Clear Mock Author Stats
            for (let i = 1; i <= 5; i++) {
                const id = `mock-author-${i}`;
                const ref = doc(db, 'stats_daily_author', `${todayStr}_${id}`);
                batch.delete(ref);
            }

            await batch.commit();
            console.log("Mock Data Cleared Successfully");
            return true;
        } catch (e) {
            console.error("Mock Data Clearing Failed:", e);
            return false;
        }
    }
};
